<template>
    <div v-if="deveMostrarModal && alertaAtivo"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" @click="fecharModal">
        <div class="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" @click.stop>
            <!-- Cabeçalho -->
            <div class="bg-admin-primary text-white p-4 flex justify-between items-center">
                <h2 class="text-xl font-bold flex items-center gap-2">
                    <Bell class="w-6 h-6" />
                    {{ alertaAtivo.titulo || 'Alerta Importante' }}
                </h2>
                <button @click="fecharModal" class="text-white hover:text-gray-200">
                    <X class="w-6 h-6" />
                </button>
            </div>

            <!-- Conteúdo -->
            <div class="p-6 space-y-4">


                <!-- Descrição -->
                <p v-if="alertaAtivo.descricao" class="text-gray-700 leading-relaxed">
                    {{ alertaAtivo.descricao }}
                </p>

                <!-- Imagem -->
                <div v-if="alertaAtivo.tipo === 'IMAGE' && alertaAtivo.link" class="mt-4">
                    <img :src="alertaAtivo.link" :alt="alertaAtivo.titulo"
                        class="w-full h-96 object-cover rounded-lg shadow" @error="handleImageError" />
                </div>

                <!-- Vídeo -->
                <div v-if="alertaAtivo.tipo === 'VIDEO' && alertaAtivo.link" class="mt-4 aspect-video">
                    <iframe :src="getVideoEmbedUrl(alertaAtivo.link)" frameborder="0" allow="autoplay; encrypted-media"
                        allowfullscreen class="w-full h-full rounded-lg shadow">
                    </iframe>
                </div>

                <!-- Link -->
                <div v-if="alertaAtivo.link && !['IMAGE', 'VIDEO'].includes(alertaAtivo.tipo)" class="mt-4">
                    <a :href="alertaAtivo.link" target="_blank" rel="noopener noreferrer" class="btn btn-primary gap-2">
                        <ExternalLink class="w-4 h-4" />
                        Acessar Link
                    </a>
                </div>

                <!-- Período -->
                <div v-if="alertaAtivo.data_inicio || alertaAtivo.data_fim" class="text-sm text-gray-500 italic">
                    <span v-if="alertaAtivo.data_inicio">Válido a partir de: {{ formatarData(alertaAtivo.data_inicio)
                        }}</span>
                    <span v-if="alertaAtivo.data_inicio && alertaAtivo.data_fim" class="mx-1">|</span>
                    <span v-if="alertaAtivo.data_fim">Válido até: {{ formatarData(alertaAtivo.data_fim) }}</span>
                </div>
            </div>

            <!-- Rodapé -->
            <div class="bg-gray-50 px-6 py-4 flex justify-end gap-3">

                <button @click="verProximo" v-if="temProximo" class="btn btn-outline btn-admin-primary gap-2">
                    Próximo
                    <ChevronRight class="w-4 h-4" />
                </button>

            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Bell, X, ExternalLink, ChevronRight, ChevronLeft } from 'lucide-vue-next';

const props = defineProps({
    alertas: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['fechado']);

const indiceAtual = ref(0);
const deveMostrarModal = ref(true);

// Alerta atual
const alertaAtivo = computed(() => {
    if (!props.alertas || props.alertas.length === 0) return null;
    return props.alertas[indiceAtual.value];
});

// Tem próximo alerta
const temProximo = computed(() => {
    return props.alertas && indiceAtual.value < props.alertas.length - 1;
});

// Tem alerta anterior
const temAnterior = computed(() => {
    return indiceAtual.value > 0;
});

// Verificar sessionStorage ao montar
onMounted(() => {
    const modalFechado = sessionStorage.getItem('alerta_modal_fechado');
    deveMostrarModal.value = !modalFechado;
});

// Fechar modal
const fecharModal = () => {
    deveMostrarModal.value = false;
    sessionStorage.setItem('alerta_modal_fechado', 'true');
    emit('fechado');
};

// Ver próximo alerta
const verProximo = () => {
    if (temProximo.value) {
        indiceAtual.value++;
    }
};

// Ver alerta anterior
const verAnterior = () => {
    if (temAnterior.value) {
        indiceAtual.value--;
    }
};

// Classes de badge por tipo
const getBadgeClass = (tipo) => {
    const classes = {
        'VIDEO': 'badge-primary',
        'TEXTO': 'badge-secondary',
        'IMAGE': 'badge-accent'
    };
    return classes[tipo] || 'badge-neutral';
};

// Labels por tipo
const getTipoLabel = (tipo) => {
    const labels = {
        'VIDEO': 'Vídeo',
        'TEXTO': 'Texto',
        'IMAGE': 'Imagem'
    };
    return labels[tipo] || tipo;
};

// Converter URL do YouTube para embed
const getVideoEmbedUrl = (url) => {
    try {
        const videoId = url.split('v=')[1]?.split('&')[0];
        return `https://www.youtube.com/embed/${videoId}`;
    } catch {
        return url;
    }
};

// Formatar data
const formatarData = (data) => {
    if (!data) return '';
    const date = new Date(data);
    return date.toLocaleDateString('pt-BR');
};

// Tratar erro de imagem
const handleImageError = (event) => {
    event.target.src = '/images/placeholder.svg';
};
</script>

<style scoped>
.fixed {
    animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
